import { SignalTies } from '@cadstrom/cadstrom-shared';
import { SignalFloatingBehavior } from '@cadstrom/sigma-engine-shared';
import ButtonBase from '@mui/material/ButtonBase';
import clsx from 'clsx';
import { useState } from 'react';

import { ModelText } from '#components/model-text/model-text.js';
import { SignalTieChip } from '#components/signal-tie-chip/signal-tie-chip.js';
import { DirectiveApplyBar } from './directive-apply-bar.js';
import { useParsedDirectiveRows } from './use-parsed-directive-rows.js';

export interface FloatsAndTiesDirectiveTie {
	targetSignalId?: string;
	targetSignalName: string;
	connectionType: string;
	componentType?: string | null;
	value?: number | null;
	variance?: number | null;
	varianceType?: string | null;
	minimum?: number | null;
	maximum?: number | null;
}

export interface FloatsAndTiesDirectiveRow {
	signalId?: string;
	signalName: string;
	floatingBehavior?: string | null;
	ties?: FloatsAndTiesDirectiveTie[];
}

export interface FloatsAndTiesTableMarkdownItemProps {
	/** Raw JSON string encoding an array of {@link FloatsAndTiesDirectiveRow} entries. */
	jsonData?: string;
}

const DASH = '—';

const editInputClass =
	'w-full min-w-10 bg-transparent border-b border-gray-a6 focus:outline-none focus:border-blue-9 px-1 py-0.5';

/**
 * Markdown directive component that renders a compact floats-and-ties table inside a workbench message.
 *
 * Parses `jsonData` as `Array<FloatsAndTiesDirectiveRow>` and shows one row per signal with its
 * floating-behaviour verdict and required tie connections. Rows are editable inline before
 * applying, including adding and removing ties. Renders `null` when `jsonData` is absent or not
 * valid JSON.
 *
 * @param jsonData - Raw JSON string containing the floats-and-ties array.
 */
export function FloatsAndTiesTableMarkdownItem({ jsonData }: FloatsAndTiesTableMarkdownItemProps) {
	const [rows, setRows] = useParsedDirectiveRows(jsonData, parseFloatsAndTies);
	const [isEditing, setIsEditing] = useState(false);

	if (!rows) {
		return null;
	}

	function updateRow(index: number, patch: Partial<FloatsAndTiesDirectiveRow>) {
		setRows((prev) => (prev ? prev.map((r, i) => (i === index ? { ...r, ...patch } : r)) : null));
	}

	function updateTie(rowIndex: number, tieIndex: number, patch: Partial<FloatsAndTiesDirectiveTie>) {
		setRows((prev) =>
			prev ?
				prev.map((row, i) =>
					i === rowIndex ?
						{ ...row, ties: (row.ties ?? []).map((tie, j) => (j === tieIndex ? { ...tie, ...patch } : tie)) }
					:	row,
				)
			:	null,
		);
	}

	function removeTie(rowIndex: number, tieIndex: number) {
		setRows((prev) =>
			prev ?
				prev.map((row, i) =>
					i === rowIndex ? { ...row, ties: (row.ties ?? []).filter((_, j) => j !== tieIndex) } : row,
				)
			:	null,
		);
	}

	function addTie(rowIndex: number) {
		setRows((prev) =>
			prev ?
				prev.map((row, i) =>
					i === rowIndex ?
						{
							...row,
							ties: [
								...(row.ties ?? []),
								{ targetSignalName: '', connectionType: SignalTies.SignalTieConnectionType.Direct },
							],
						}
					:	row,
				)
			:	null,
		);
	}

	return (
		<>
			<div className='my-1 overflow-x-auto'>
				<table className='w-full border-collapse text-sm'>
					<thead>
						<tr className='border-b border-gray-a4'>
							<th className='min-w-24 px-2 py-1 text-left font-semibold whitespace-nowrap text-gray-11'>Signal</th>
							<th className='min-w-24 px-2 py-1 text-left font-semibold whitespace-nowrap text-gray-11'>Float</th>
							<th className='min-w-40 px-2 py-1 text-left font-semibold whitespace-nowrap text-gray-11'>Ties</th>
							<th className='px-2 py-1 text-right'>
								<ButtonBase
									className='text-xs font-normal tracking-normal text-gray-11 normal-case hover:text-gray-12'
									onClick={() => setIsEditing((v) => !v)}
								>
									{isEditing ? 'Done' : 'Edit'}
								</ButtonBase>
							</th>
						</tr>
					</thead>
					<tbody>
						{rows.map((row, index) => (
							<tr
								key={index}
								className={clsx('border-b border-gray-a3', index % 2 === 0 ? 'bg-transparent' : 'bg-gray-a2')}
							>
								<td className='px-2 py-1 align-top font-mono whitespace-nowrap text-gray-12'>
									{isEditing ?
										<input
											type='text'
											value={row.signalName}
											onChange={(e) => updateRow(index, { signalName: e.target.value })}
											className={editInputClass}
										/>
									:	row.signalName}
								</td>
								<td className='px-2 py-1 align-top text-gray-12'>
									{isEditing ?
										<select
											value={row.floatingBehavior ?? ''}
											onChange={(e) => updateRow(index, { floatingBehavior: e.target.value || null })}
											className={editInputClass}
										>
											<option value=''>{DASH}</option>
											{Object.values(SignalFloatingBehavior).map((value) => (
												<option key={value} value={value}>
													{value}
												</option>
											))}
										</select>
									:	(row.floatingBehavior ?? DASH)}
								</td>
								<td className='px-2 py-1 align-top text-gray-12'>
									{isEditing ?
										<div className='flex flex-col gap-1'>
											{(row.ties ?? []).map((tie, tieIndex) => (
												<div key={tieIndex} className='flex items-center gap-1'>
													<input
														type='text'
														placeholder='to signal'
														value={tie.targetSignalName}
														onChange={(e) => updateTie(index, tieIndex, { targetSignalName: e.target.value })}
														className={clsx(editInputClass, 'w-24')}
													/>
													<select
														value={
															tie.connectionType === String(SignalTies.SignalTieConnectionType.Direct) ?
																'direct'
															:	(tie.componentType ?? '')
														}
														onChange={(e) => {
															const thru = e.target.value;
															updateTie(
																index,
																tieIndex,
																thru === 'direct' ?
																	{ connectionType: SignalTies.SignalTieConnectionType.Direct, componentType: null }
																:	{
																		connectionType: SignalTies.SignalTieConnectionType.ThroughComponent,
																		componentType: thru,
																	},
															);
														}}
														className={clsx(editInputClass, 'w-28')}
													>
														<option value='direct'>Direct</option>
														<option value={SignalTies.SignalTieComponentType.Resistor}>Resistor</option>
														<option value={SignalTies.SignalTieComponentType.Capacitor}>Capacitor (nonpol.)</option>
														<option value={SignalTies.SignalTieComponentType.CapacitorPolarized}>
															Capacitor (pol.)
														</option>
														<option value={SignalTies.SignalTieComponentType.Inductor}>Inductor</option>
													</select>
													<input
														type='number'
														placeholder='value'
														value={tie.value ?? ''}
														onChange={(e) => updateTie(index, tieIndex, { value: parseNumericInput(e.target.value) })}
														className={clsx(editInputClass, 'w-20')}
													/>
													<input
														type='number'
														placeholder='min'
														value={tie.minimum ?? ''}
														onChange={(e) => updateTie(index, tieIndex, { minimum: parseNumericInput(e.target.value) })}
														className={clsx(editInputClass, 'w-16')}
													/>
													<input
														type='number'
														placeholder='max'
														value={tie.maximum ?? ''}
														onChange={(e) => updateTie(index, tieIndex, { maximum: parseNumericInput(e.target.value) })}
														className={clsx(editInputClass, 'w-16')}
													/>
													<ButtonBase
														aria-label='Remove tie'
														className='px-1 text-gray-11 hover:text-gray-12'
														onClick={() => removeTie(index, tieIndex)}
													>
														✕
													</ButtonBase>
												</div>
											))}
											<ButtonBase
												className='self-start text-xs font-normal tracking-normal text-gray-11 normal-case hover:text-gray-12'
												onClick={() => addTie(index)}
											>
												+ Add tie
											</ButtonBase>
										</div>
									: (row.ties ?? []).length === 0 ?
										DASH
									:	<div className='flex flex-wrap gap-1'>
											{(row.ties ?? []).map((tie, tieIndex) => {
												const flatTie = toFlatTieForDisplay(tie);
												return flatTie ?
														<SignalTieChip
															key={tieIndex}
															tie={flatTie}
															target={<ModelText value={tie.targetSignalName} />}
														/>
													:	<span key={tieIndex}>{tie.targetSignalName}</span>;
											})}
										</div>
									}
								</td>
								<td />
							</tr>
						))}
					</tbody>
				</table>
			</div>
			<DirectiveApplyBar data={rows} configId='floatsAndTies' />
		</>
	);
}

function parseFloatsAndTies(jsonData: string): FloatsAndTiesDirectiveRow[] | null {
	try {
		const parsed: unknown = JSON.parse(jsonData);
		if (!Array.isArray(parsed)) {
			return null;
		}
		const rows = parsed.filter((item): item is FloatsAndTiesDirectiveRow => {
			if (typeof item !== 'object' || item === null) {
				return false;
			}
			const record = item as Record<string, unknown>;
			if (typeof record['signalName'] !== 'string') {
				return false;
			}
			// The rows are model-authored JSON, so a tie's target name is a string by assertion only, and a
			// number or an object would throw once it reaches ModelText.
			const ties = record['ties'];
			return ties === undefined || ties === null || (Array.isArray(ties) && ties.every(hasStringTargetName));
		});
		return rows.length > 0 ? rows : null;
	} catch {
		return null;
	}
}

/**
 * Checks that a parsed tie names its target with a string.
 *
 * @param tie - An entry of a row's `ties`, straight from the directive's JSON.
 * @returns True when the tie is an object whose `targetSignalName` is a string.
 */
function hasStringTargetName(tie: unknown): boolean {
	return (
		typeof tie === 'object' && tie !== null && typeof (tie as Record<string, unknown>)['targetSignalName'] === 'string'
	);
}

/** Builds a display-only flat tie for {@link SignalTieChip}; null when the tie's enum fields don't parse. */
function toFlatTieForDisplay(tie: FloatsAndTiesDirectiveTie): SignalTies.FlatSignalTie | null {
	const connectionTypes = Object.values(SignalTies.SignalTieConnectionType) as string[];
	if (!connectionTypes.includes(tie.connectionType)) {
		return null;
	}
	const connectionType = tie.connectionType as SignalTies.SignalTieConnectionType;

	let componentType: SignalTies.SignalTieComponentType | null = null;
	if (tie.componentType !== null && tie.componentType !== undefined && tie.componentType !== '') {
		const componentTypes = Object.values(SignalTies.SignalTieComponentType) as string[];
		if (!componentTypes.includes(tie.componentType)) {
			return null;
		}
		componentType = tie.componentType as SignalTies.SignalTieComponentType;
	}

	let componentValue: SignalTies.FlatSignalTie['componentValue'] = null;
	if (typeof tie.value === 'number') {
		componentValue = {
			type: 'absolute',
			value: tie.value,
			variance: tie.variance ?? null,
			varianceType:
				typeof tie.variance === 'number' ?
					tie.varianceType === SignalTies.SignalTieVarianceType.Relative ?
						SignalTies.SignalTieVarianceType.Relative
					:	SignalTies.SignalTieVarianceType.Absolute
				:	null,
		};
	} else if (typeof tie.minimum === 'number' || typeof tie.maximum === 'number') {
		componentValue = { type: 'range', min: tie.minimum ?? null, max: tie.maximum ?? null };
	}

	return {
		toSignalId: tie.targetSignalId ?? tie.targetSignalName,
		connectionType,
		componentType,
		componentValue,
	};
}

function parseNumericInput(value: string): number | null {
	if (value === '') {
		return null;
	}
	const n = Number(value);
	return Number.isFinite(n) ? n : null;
}
